// Document Metadata - Frontend
// Mirrors backend CompanyDocumentMetadata for auto-filling document sphere and validity

import { CompanyDocumentType, DocumentSphere } from '../types/documents';

export interface DocumentMetadata {
  sphere: DocumentSphere;
  validityDays: number | null; // null means permanent (no expiration)
}

// Maps document types to their metadata
const DOCUMENT_METADATA: Record<CompanyDocumentType, DocumentMetadata> = {
  // Original document types
  [CompanyDocumentType.ECNPJ]: { sphere: DocumentSphere.Federal, validityDays: null },
  [CompanyDocumentType.ContratoSocial]: { sphere: DocumentSphere.Federal, validityDays: null },
  [CompanyDocumentType.Alvara]: { sphere: DocumentSphere.Municipal, validityDays: 365 },
  [CompanyDocumentType.InscricaoEstadual]: { sphere: DocumentSphere.Estadual, validityDays: null },
  [CompanyDocumentType.InscricaoMunicipal]: { sphere: DocumentSphere.Municipal, validityDays: null },
  [CompanyDocumentType.CartaoCNPJ]: { sphere: DocumentSphere.Federal, validityDays: null },
  [CompanyDocumentType.DARFDAS]: { sphere: DocumentSphere.Federal, validityDays: null },
  [CompanyDocumentType.CertidaoNegativa]: { sphere: DocumentSphere.Federal, validityDays: 180 },
  [CompanyDocumentType.ContratoLocacao]: { sphere: DocumentSphere.Permanente, validityDays: null },
  [CompanyDocumentType.ComprovanteEndereco]: { sphere: DocumentSphere.Permanente, validityDays: null },

  // New document types with specific sphere and validity
  [CompanyDocumentType.CertidaoConjuntaReceitaFederalPGFN]: { sphere: DocumentSphere.Federal, validityDays: 180 },
  [CompanyDocumentType.CertificadoRegularidadeFGTS]: { sphere: DocumentSphere.Federal, validityDays: 30 },
  [CompanyDocumentType.CertidaoNegativaDebitosTrabalhistasCNDT]: { sphere: DocumentSphere.Federal, validityDays: 180 },
  [CompanyDocumentType.CertidaoNegativaDebitosEstaduais]: { sphere: DocumentSphere.Estadual, validityDays: 30 },
  [CompanyDocumentType.CertidaoRegularidadeICMS]: { sphere: DocumentSphere.Estadual, validityDays: 30 },
  [CompanyDocumentType.CertidaoNegativaDebitosMunicipais]: { sphere: DocumentSphere.Municipal, validityDays: 30 },
  [CompanyDocumentType.CertidaoFalenciaConcordataRecuperacaoJudicial]: { sphere: DocumentSphere.Judicial, validityDays: 30 },
  [CompanyDocumentType.CertificadoEntidadeClasse]: { sphere: DocumentSphere.Profissional, validityDays: 365 },
  [CompanyDocumentType.CertidaoRegularidadeINSS]: { sphere: DocumentSphere.Federal, validityDays: 180 },
  [CompanyDocumentType.AlvaraFuncionamento]: { sphere: DocumentSphere.Municipal, validityDays: 365 },
  [CompanyDocumentType.AlvaraVigilanciaSanitaria]: { sphere: DocumentSphere.Municipal, validityDays: 365 },
  [CompanyDocumentType.LicencaCorpoBombeiros]: { sphere: DocumentSphere.Municipal, validityDays: 1095 }, // 3 years

  [CompanyDocumentType.Other]: { sphere: DocumentSphere.Permanente, validityDays: null },
};

export class CompanyDocumentMetadata {
  /**
   * Gets metadata for a document type
   */
  static getMetadata(documentType: CompanyDocumentType): DocumentMetadata | null {
    return DOCUMENT_METADATA[documentType] || null;
  }

  /**
   * Gets the sphere for a document type
   */
  static getSphere(documentType: CompanyDocumentType): DocumentSphere {
    return DOCUMENT_METADATA[documentType]?.sphere || DocumentSphere.Permanente;
  }

  /**
   * Gets the validity days for a document type
   */
  static getValidityDays(documentType: CompanyDocumentType): number | null {
    return DOCUMENT_METADATA[documentType]?.validityDays || null;
  }

  /**
   * Checks if a document type has an expiration
   */
  static hasExpiration(documentType: CompanyDocumentType): boolean {
    const validityDays = this.getValidityDays(documentType);
    return validityDays !== null && validityDays > 0;
  }

  /**
   * Calculates expiration date from start date and document type
   */
  static calculateExpirationDate(documentType: CompanyDocumentType, startDate: Date): Date | null {
    const validityDays = this.getValidityDays(documentType);
    if (!validityDays) return null;

    const expirationDate = new Date(startDate);
    expirationDate.setDate(expirationDate.getDate() + validityDays);
    return expirationDate;
  }
}
